import { FilterEnum, IFilter } from 'components/Filter/SingleFilter'
import { HonestTable, IHonestTableColumn, IHonestTableMenuOption } from 'components/HonestTable/HonestTable'
import { Cell, RightCell } from 'components/HonestTable/HonestTable.styled'
import { NavBar } from 'components/NavBar/NavBar'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { setConfirmModal } from 'features/global/globalSlice'
import { deleteOperationTrigger, getOperationTriggers, toggleOperationTrigger } from 'features/operations/operationTriggersSlice'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AppDispatch, RootState } from 'store'
import { Flex, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { IOpsTriggerRow, OpsTriggerMode } from 'types/operationsControl.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { getParams } from 'utils/helpers'
import { EmptyStateWrap, MonoCondition } from './OperationTriggers.styled'

const MODE_META: { [k in OpsTriggerMode]: { label: string; tone: IStatusPill['status'] } } = {
    COMPLETION: { label: 'Completion', tone: 'success' },
    SCHEDULED: { label: 'Scheduled', tone: 'info' },
    THRESHOLD: { label: 'Threshold', tone: 'warning' },
    EVENT: { label: 'Event', tone: 'danger' },
}

const MODE_BY_FILTER_ID: { [k: string]: OpsTriggerMode } = {
    '2': 'COMPLETION',
    '3': 'SCHEDULED',
    '4': 'THRESHOLD',
    '5': 'EVENT',
}

const ACTION_BY_FILTER_ID: { [k: string]: string } = {
    '2': 'Create task',
    '3': 'Create workflow',
    '4': 'Raise issue',
    '5': 'Notify role',
}

const PRIORITY_BY_FILTER_ID: { [k: string]: string } = {
    '2': 'normal',
    '3': 'high',
    '4': 'critical',
}

const filterTriggers = (rows: IOpsTriggerRow[], params: any) =>
    rows.filter((row) => {
        if (params.mode && row.mode !== MODE_BY_FILTER_ID[String(params.mode)]) return false
        if (String(params.status) === '2' && !row.enabled) return false
        if (String(params.status) === '3' && row.enabled) return false
        if (params.action && row.action !== ACTION_BY_FILTER_ID[String(params.action)]) return false
        if (params.priority && row.priority !== PRIORITY_BY_FILTER_ID[String(params.priority)]) return false
        return true
    })

const filters: IFilter[] = [
    {
        type: FilterEnum.FIXED_LIST,
        name: 'Mode',
        state: null,
        propName: 'mode',
        sd: true,
        single: true,
        filterOptions: [
            { name: 'Completion', id: '2' },
            { name: 'Scheduled', id: '3' },
            { name: 'Threshold', id: '4' },
            { name: 'Event', id: '5' },
        ],
    },
    {
        type: FilterEnum.FIXED_LIST,
        name: 'Status',
        state: null,
        propName: 'status',
        sd: true,
        single: true,
        filterOptions: [
            { name: 'Enabled', id: '2' },
            { name: 'Disabled', id: '3' },
        ],
    },
    {
        type: FilterEnum.FIXED_LIST,
        name: 'Action',
        state: null,
        propName: 'action',
        sd: true,
        single: true,
        filterOptions: [
            { name: 'Create Task', id: '2' },
            { name: 'Create Workflow', id: '3' },
            { name: 'Raise Issue', id: '4' },
            { name: 'Notify Role', id: '5' },
        ],
    },
    {
        type: FilterEnum.FIXED_LIST,
        name: 'Priority',
        state: null,
        propName: 'priority',
        sd: true,
        single: true,
        filterOptions: [
            { name: 'Normal', id: '2' },
            { name: 'High', id: '3' },
            { name: 'Critical', id: '4' },
        ],
    },
]

export const OperationTriggers = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const { triggers } = useSelector((state: RootState) => state.operationTriggers)
    const [params, setParams] = useState<any>(getParams({ location }))
    const shown = useMemo(() => filterTriggers(triggers, params), [triggers, params])

    useEffect(() => {
        dispatch(getOperationTriggers())
    }, [dispatch])

    const handleToggle = (item: IOpsTriggerRow) => {
        dispatch(toggleOperationTrigger({ id: item.id, enabled: !item.enabled }))
        toast.success(item.enabled ? 'Trigger disabled' : 'Trigger enabled')
    }

    const handleDelete = (item: IOpsTriggerRow) => {
        dispatch(
            setConfirmModal({
                isOpen: true,
                onSave: () => {
                    dispatch(deleteOperationTrigger(item.id))
                    toast.success('Trigger deleted')
                },
                word: 'delete trigger',
            }),
        )
    }

    const columns: IHonestTableColumn<IOpsTriggerRow>[] = [
        {
            id: 'type',
            header: 'Type',
            sticky: true,
            minWidth: '8rem',
            cell: (item) => <StatusPill status={MODE_META[item.mode].tone}>{MODE_META[item.mode].label}</StatusPill>,
        },
        {
            id: 'trigger',
            header: 'Trigger',
            minWidth: '16rem',
            cell: (item) => (
                <Cell>
                    <BodyText weight='600'>{item.name}</BodyText>
                    <OtherText grey>{`${item.scope} · ${item.priority}`}</OtherText>
                </Cell>
            ),
        },
        {
            id: 'condition',
            header: 'Condition',
            minWidth: '14rem',
            cell: (item) => <MonoCondition>{item.condition}</MonoCondition>,
        },
        {
            id: 'action',
            header: 'Action',
            minWidth: '14rem',
            cell: (item) => <BodyText>{`${item.action} → ${item.actionTarget}`}</BodyText>,
        },
        {
            id: 'fires',
            header: 'Fires',
            minWidth: '7rem',
            cell: (item) => (
                <Cell>
                    <BodyText>{item.fireCount}</BodyText>
                    <OtherText grey>{item.lastFired || '—'}</OtherText>
                </Cell>
            ),
        },
        {
            id: 'nextExpected',
            header: 'Next expected',
            minWidth: '10rem',
            cell: (item) => <BodyText>{item.nextExpected || '—'}</BodyText>,
        },
        {
            id: 'status',
            header: 'Status',
            minWidth: '8rem',
            cell: (item) => (
                <StatusPill status={item.enabled ? 'success' : 'neutral'}>
                    {item.enabled ? 'Enabled' : 'Disabled'}
                </StatusPill>
            ),
        },
        {
            id: 'toggle',
            header: '',
            align: 'right',
            fill: true,
            cell: (item) => (
                <RightCell>
                    <Button variant='black' size='sm' onClick={() => handleToggle(item)}>
                        {item.enabled ? 'Disable' : 'Enable'}
                    </Button>
                </RightCell>
            ),
        },
    ]

    const menuOptions: IHonestTableMenuOption<IOpsTriggerRow>[] = [
        { label: 'Delete', color: tokens.status.danger.fg, onClick: (item) => handleDelete(item) },
    ]

    return (
        <>
            <NavBar
                enableTemporaryManualClosing
                filterOptions={filters}
                params={params}
                setParams={setParams}
            />
            <PageWrapper>
                <Flex width='100%' between mb='32'>
                    <Flex column gap='0.5rem'>
                        <Flex center gap='1rem'>
                            <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
                                Triggers & automation
                            </Head1>
                        </Flex>
                    </Flex>
                    <Button variant='primary' onClick={() => navigate('/operations/triggers/add')}>
                        Add
                    </Button>
                </Flex>
                {triggers.length ? (
                    <HonestTable
                        columns={columns}
                        data={shown}
                        keyField='id'
                        menuOptions={menuOptions}
                        emptyMessage='No triggers match the current filters'
                    />
                ) : (
                    <EmptyStateWrap>
                        <OtherText grey>No automation triggers configured.</OtherText>
                        <Button variant='primary' onClick={() => navigate('/operations/triggers/add')}>
                            Create trigger
                        </Button>
                    </EmptyStateWrap>
                )}
            </PageWrapper>
        </>
    )
}
